import React from 'react';

import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import JsCookie from 'js-cookie';
import { useRouter } from 'next/router';
import { NavLinkInterface } from 'server/config-cache/types';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { logout } from 'src/services/auth-service/logout.service';
import { CART_ID_COOKIE_KEY } from 'src/utils/constants';

import ConfigContext from '../../context/config';
import DesktopNavBar from './DesktopNavBar';
import MobileNavBar from './MobileNavBar';

const NavBarHeader: React.FC = () => {
  const router = useRouter();

  // non-nulls are safe here as we always provide values to our context providers.
  const configContext = useNonNullContext(ConfigContext);
  const { user, setUser } = useNonNullContext(UserContext);

  // use this for conditional rendering
  const isAuthenticated = user !== null;

  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const {
    authenticated: authenticatedTenantConfig,
    unAuthenticated: unauthenticatedTenantConfig,
  } = configContext.navBar;

  const defaultAuthLinks = {
    leftLinks: [] as NavLinkInterface[],
    rightLinks: [
      {
        displayText: 'Dashboard',
        url: '/dashboard',
      },
    ],
  };

  const authedNavBarLinks = {
    leftLinks: [
      ...defaultAuthLinks.leftLinks,
      ...authenticatedTenantConfig.leftLinks,
    ],
    rightLinks: [
      ...defaultAuthLinks.rightLinks,
      ...authenticatedTenantConfig.rightLinks,
    ],
  };

  const { leftLinks, rightLinks } = isAuthenticated
    ? authedNavBarLinks
    : unauthenticatedTenantConfig;

  // userMenuLinks are only applicable when authenticated so don't need ternary.
  const userMenuLinks = [
    ...authenticatedTenantConfig.userMenuLinks,
    { displayText: 'My Credits', url: '/my-credits' },
    { displayText: 'Order History', url: '/payment-history' },
  ];

  const { logIn } = unauthenticatedTenantConfig;

  const handleLogout = async () => {
    await logout();
    JsCookie.remove(CART_ID_COOKIE_KEY);
    await router.push('/login');
    setUser(null);
  };

  const defaultCtaLink: NavLinkInterface = {
    displayText: 'Get Started',
    url: '/get-started',
  };

  if (isSmallScreen) {
    return (
      <MobileNavBar
        drawerMenu={{
          cta: defaultCtaLink,
          links: leftLinks,
        }}
        isAuthenticated={isAuthenticated}
        loginDisplayText={logIn.displayText}
        logout={handleLogout}
        userMenuLinks={{
          account: userMenuLinks,
          bottom: leftLinks,
          top: rightLinks,
        }}
      />
    );
  }

  return (
    <DesktopNavBar
      isAuthenticated={isAuthenticated}
      loginDisplayText={logIn.displayText}
      logout={handleLogout}
      leftLinks={leftLinks}
      rightLinks={rightLinks}
      accountLinks={userMenuLinks}
      ctaLink={defaultCtaLink}
    />
  );
};

export default NavBarHeader;
